import apiClient from "../../client/apiClient";
import { appendQueryParams } from "../../utils";

// Types
export interface CourseSubCategory {
  _id?: string;
  description: string;
  marks: number;
}

export interface CourseStructure {
  _id: string;
  session: {
    _id: string;
    title: string;
  };
  branch: {
    _id: string;
    name: string;
  };
  system: {
    _id: string;
    name: string;
  };
  class: {
    _id: string;
    name: string;
    order?: number;
  };
  classGroup: {
    _id: string;
    name: string;
  } | null;
  breakdown: {
    _id: string;
    title: string;
  };
  course: {
    _id: string;
    title: string;
    courseIdentifier: string;
    displayOrder?: number;
  };
  section: {
    _id: string;
    name: string;
  };
  allowSum: boolean;
  includeInTotal: boolean;
  showGradeOnly: boolean;
  totalMarks: number;
  subCategories: CourseSubCategory[];
  termPercentages?: { term: string; percentage: number; isFinal: boolean }[];
  isDeleted: boolean;
  deletedAt?: string;
  deletedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PaginationData {
  total: number;
  totalPages: number;
  currentPage: number;
  limit: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface CourseStructureResponse {
  success: boolean;
  message?: string;
  data: CourseStructure | CourseStructure[];
  pagination?: PaginationData;
}

export interface CourseStructureListResponse {
  success: boolean;
  message?: string;
  data: CourseStructure[];
  pagination: PaginationData;
}

export interface CreateCourseStructureData {
  sessionId?: string;
  session?: string;
  branchId?: string;
  branch?: string;
  systemId?: string;
  system?: string;
  classIds?: string[];
  classId?: string;
  class?: string;
  courseId?: string;
  course?: string;
  breakdownId?: string;
  breakdown?: string;
  sectionId?: string;
  section?: string;
  allowSum?: boolean;
  includeInTotal?: boolean;
  showGradeOnly?: boolean;
  totalMarks: number;
  subCategories?: CourseSubCategory[];
}

export interface UpdateCourseStructureData {
  allowSum?: boolean;
  includeInTotal?: boolean;
  showGradeOnly?: boolean;
  totalMarks?: number;
  sectionId?: string;
  section?: string;
  subCategories?: CourseSubCategory[];
}

export interface Breakdown {
  _id: string;
  title: string;
  startDate: string;
  endDate: string;
  session?: {
    _id: string;
    title: string;
  };
  system?: {
    _id: string;
    name: string;
  };
  branch?: {
    _id: string;
    name: string;
  };
}

export interface BreakdownsResponse {
  success: boolean;
  message?: string;
  data: Breakdown[];
}

// API Endpoints
const COURSE_STRUCTURE_ENDPOINT = {
  BASE: "/course-structure",
  BY_ID: (id: string) => `/course-structure/${id}`,
  BREAKDOWNS: "/course-structure/breakdowns",
};

/**
 * Create Course Structure(s)
 * Supports bulk creation when classIds array is provided
 */
/**
 * Save one class's whole marks allocation in a single call — every subject,
 * across every section that class runs this session.
 *
 * UPSERT ONLY: a subject left out keeps whatever it had. Removing a subject is
 * done from Course Structures (class subjects), which cascades properly.
 */
export const bulkSaveCourseStructures = async (data: {
  session: string;
  class: string;
  // Optional. A class split into streams (e.g. Biology/Computer) must save
  // its allocation per group -- omit/null for an ungrouped class.
  classGroup?: string | null;
  breakdown: string;
  subjects: Array<{
    course: string;
    totalMarks: number;
    includeInTotal?: boolean;
    allowSum?: boolean;
    showGradeOnly?: boolean;
    // `_id` on an existing subcategory must survive the round trip — student
    // marks are stored against it, and dropping it orphans them.
    subCategories?: Array<{ _id?: string; description: string; marks: number }>;
  }>;
}): Promise<{
  success: boolean;
  message: string;
  created: number;
  updated: number;
  sections: number;
}> => {
  const response = await apiClient({
    url: `${COURSE_STRUCTURE_ENDPOINT.BASE}/bulk`,
    method: "POST",
    data,
  });
  return response.data;
};

export const createCourseStructure = async (
  data: CreateCourseStructureData
): Promise<CourseStructureResponse> => {
  const response = await apiClient({
    url: COURSE_STRUCTURE_ENDPOINT.BASE,
    method: "POST",
    data,
  });
  return response.data;
};

/**
 * List Course Structures with filters and pagination
 */
export const getAllCourseStructures = async ({
  page = 1,
  limit = 10,
  sortBy = "createdAt",
  order = "desc",
  session,
  branch,
  system,
  class: classId,
  classGroup,
  breakdown,
  course,
  section,
}: {
  page?: number;
  limit?: number;
  sortBy?: string;
  order?: "asc" | "desc";
  session?: string;
  branch?: string;
  system?: string;
  class?: string;
  // "null" (string) asks for ungrouped rows only; a Group id scopes to it;
  // omitted returns every group merged.
  classGroup?: string | "null";
  breakdown?: string;
  course?: string;
  section?: string;
} = {}): Promise<CourseStructureListResponse> => {
  const params: Record<string, any> = {
    page,
    limit,
    sortBy,
    order,
    session,
    branch,
    system,
    class: classId,
    classGroup,
    breakdown,
    course,
    section,
  };

  // Remove undefined/null/empty values
  Object.keys(params).forEach((key) => {
    if (
      params[key] === undefined ||
      params[key] === null ||
      params[key] === ""
    ) {
      delete params[key];
    }
  });

  const response = await apiClient({
    url: appendQueryParams(COURSE_STRUCTURE_ENDPOINT.BASE, params),
    method: "GET",
  });
  return response.data;
};

/**
 * Get Course Structure by ID
 */
export const getCourseStructureById = async (
  id: string
): Promise<CourseStructureResponse> => {
  const response = await apiClient({
    url: COURSE_STRUCTURE_ENDPOINT.BY_ID(id),
    method: "GET",
  });
  return response.data;
};

/**
 * Update Course Structure
 */
export const updateCourseStructure = async (
  id: string,
  data: UpdateCourseStructureData
): Promise<CourseStructureResponse> => {
  const response = await apiClient({
    url: COURSE_STRUCTURE_ENDPOINT.BY_ID(id),
    method: "PUT",
    data,
  });
  return response.data;
};

/**
 * Soft delete Course Structure
 */
export const deleteCourseStructure = async (
  id: string
): Promise<{ success: boolean; message: string; data: { id: string } }> => {
  const response = await apiClient({
    url: COURSE_STRUCTURE_ENDPOINT.BY_ID(id),
    method: "DELETE",
  });
  return response.data;
};

/**
 * Get Breakdowns (Terms) by Class IDs
 */
export const getBreakdownsByClasses = async (
  classIds: string[]
): Promise<BreakdownsResponse> => {
  const params: Record<string, any> = {
    classIds: classIds.join(","),
  };

  const response = await apiClient({
    url: appendQueryParams(COURSE_STRUCTURE_ENDPOINT.BREAKDOWNS, params),
    method: "GET",
  });
  return response.data;
};
